import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContentGenerator = () => {
  const [formData, setFormData] = useState({
    content_type: 'product_review',
    product_name: '',
    product_description: '',
    target_audience: '',
    tone: 'professional',
    keywords: '',
    language: 'pt-BR'
  });
  const [generatedContent, setGeneratedContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [contentHistory, setContentHistory] = useState([]);

  const contentTypes = [
    { value: 'product_review', label: 'Resenha de Produto', description: 'Resenha completa com prós e contras que convence a comprar', icon: '⭐' },
    { value: 'social_post', label: 'Post para Redes Sociais', description: 'Post viral para Instagram, Facebook, TikTok', icon: '📱' },
    { value: 'email', label: 'Email de Vendas', description: 'Email persuasivo que gera cliques e vendas', icon: '📧' },
    { value: 'landing_page', label: 'Página de Vendas', description: 'Copy de alta conversão para landing page', icon: '🎯' }
  ];

  const tones = [
    { value: 'professional', label: 'Profissional' },
    { value: 'casual', label: 'Casual' },
    { value: 'enthusiastic', label: 'Entusiasmado' },
    { value: 'authoritative', label: 'Autoritário' },
    { value: 'friendly', label: 'Amigável' }
  ];

  const languages = [
    { value: 'pt-BR', label: '🇧🇷 Português (Brasil)', flag: '🇧🇷' },
    { value: 'en-US', label: '🇺🇸 English (USA)', flag: '🇺🇸' },
    { value: 'es-ES', label: '🇪🇸 Español (España)', flag: '🇪🇸' },
    { value: 'fr-FR', label: '🇫🇷 Français (France)', flag: '🇫🇷' },
    { value: 'de-DE', label: '🇩🇪 Deutsch (Deutschland)', flag: '🇩🇪' },
    { value: 'it-IT', label: '🇮🇹 Italiano (Italia)', flag: '🇮🇹' },
    { value: 'ja-JP', label: '🇯🇵 日本語 (Japan)', flag: '🇯🇵' },
    { value: 'zh-CN', label: '🇨🇳 中文 (China)', flag: '🇨🇳' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setGeneratedContent('');

    try {
      const requestData = {
        ...formData,
        keywords: formData.keywords ? formData.keywords.split(',').map(k => k.trim()) : []
      };

      const response = await axios.post(`${API}/generate-content-public`, requestData);
      const content = response.data.content;
      setGeneratedContent(content);
      
      // Add to history
      const historyItem = {
        id: response.data.content_id,
        content_type: formData.content_type,
        product_name: formData.product_name,
        content: content,
        created_at: new Date()
      };
      setContentHistory(prev => [historyItem, ...prev.slice(0, 4)]); // Keep last 5
      
      // Success message
      alert('🎉 Conteúdo gerado com sucesso! Use este texto nas suas campanhas para gerar vendas.');
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Falha ao gerar conteúdo. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedContent);
    alert('📋 Conteúdo copiado! Cole nas suas redes sociais, blog ou campanha de email.');
  };

  const copyFromHistory = (content) => {
    navigator.clipboard.writeText(content);
    alert('📋 Conteúdo do histórico copiado!');
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">🤖 Gerador de Conteúdo com IA</h1>
        <p className="text-gray-600">Crie conteúdo profissional que vende automaticamente - Powered by Advanced AI</p>
      </div>

      {/* How It Works */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
        <h3 className="font-semibold text-blue-800 mb-3">🎯 Como Funciona o Gerador de Conteúdo:</h3>
        <div className="space-y-3 text-sm text-blue-700">
          <div className="flex items-start gap-2">
            <span className="font-bold">1️⃣</span>
            <div>
              <strong>Escolha o produto:</strong> Informe o nome do produto que você criou link em <a href="/links" className="underline text-blue-600 hover:text-blue-800">🔗 Links de Afiliado</a>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold">2️⃣</span>
            <div>
              <strong>Selecione o idioma:</strong> Gere conteúdo em Português, Inglês ou outros 6 idiomas para marketing internacional
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold">3️⃣</span>
            <div>
              <strong>IA gera o conteúdo:</strong> Cria texto profissional, persuasivo e otimizado para vendas automaticamente
            </div>
          </div>
          <div className="flex items-start gap-2">
            <span className="font-bold">4️⃣</span>
            <div>
              <strong>Copie e cole:</strong> Use o conteúdo + seu link de afiliado nas redes sociais, blog, email ou anúncios
            </div>
          </div>
        </div>
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <p className="text-yellow-800 text-sm">
            <strong>💡 Importante:</strong> Você NÃO precisa pegar os links novamente! A IA gera o <strong>conteúdo de marketing</strong> para promover seus produtos. 
            Depois de gerar, você adiciona manualmente seu <strong>link de afiliado</strong> (da página <a href="/links" className="underline">🔗 Links</a>) no final do conteúdo antes de publicar.
          </p>
        </div>
      </div>

      {/* Success Tips */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
        <h3 className="font-semibold text-green-800 mb-2">💡 Dicas para Conteúdo que Converte:</h3>
        <div className="text-sm text-green-700 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <strong>✅ FAÇA:</strong> Use "Resenha de Produto" - converte 3x mais
          </div>
          <div>
            <strong>✅ FAÇA:</strong> Seja específico no nome do produto
          </div>
          <div>
            <strong>✅ FAÇA:</strong> Use tom "Entusiasmado" para produtos caros
          </div>
          <div>
            <strong>✅ FAÇA:</strong> Adicione palavras-chave do nicho
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">⚙️ Configurações do Conteúdo</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Content Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Conteúdo
              </label>
              <div className="grid grid-cols-1 gap-3">
                {contentTypes.map(type => (
                  <label key={type.value} className="cursor-pointer">
                    <input
                      type="radio"
                      name="content_type"
                      value={type.value}
                      checked={formData.content_type === type.value}
                      onChange={(e) => handleInputChange('content_type', e.target.value)}
                      className="sr-only"
                    />
                    <div className={`p-3 border rounded-lg ${
                      formData.content_type === type.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <div className="flex items-center space-x-2">
                        <span className="text-lg">{type.icon}</span>
                        <div>
                          <div className="font-medium text-sm">{type.label}</div>
                          <div className="text-xs text-gray-600">{type.description}</div>
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Product Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Produto *
              </label>
              <input
                type="text"
                required
                value={formData.product_name}
                onChange={(e) => handleInputChange('product_name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="iPhone 15 Pro Max, Curso de Marketing Digital..."
              />
              <p className="text-xs text-gray-500 mt-1">🎯 Seja específico - ajuda a IA gerar conteúdo mais preciso</p>
            </div>

            {/* Product Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição do Produto (Opcional)
              </label>
              <textarea
                value={formData.product_description}
                onChange={(e) => handleInputChange('product_description', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Principais características, benefícios, diferenciais do produto..."
              />
            </div>

            {/* Target Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Público-Alvo (Opcional)
              </label>
              <input
                type="text"
                value={formData.target_audience}
                onChange={(e) => handleInputChange('target_audience', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Empreendedores, profissionais de tecnologia, jovens..."
              />
            </div>

            {/* Language Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                🌍 Idioma do Conteúdo
              </label>
              <select
                value={formData.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              >
                {languages.map(lang => (
                  <option key={lang.value} value={lang.value}>
                    {lang.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">🌐 Marketing internacional: gere conteúdo em 8 idiomas diferentes!</p>
            </div>

            {/* Tone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tom da Escrita
              </label>
              <select
                value={formData.tone}
                onChange={(e) => handleInputChange('tone', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {tones.map(tone => (
                  <option key={tone.value} value={tone.value}>
                    {tone.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Palavras-Chave (Opcional)
              </label>
              <input
                type="text"
                value={formData.keywords}
                onChange={(e) => handleInputChange('keywords', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="smartphone, câmera, bateria, tecnologia"
              />
              <p className="text-xs text-gray-500 mt-1">💡 Separe múltiplas palavras com vírgulas</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
                ❌ {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-4 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  🤖 IA Gerando Conteúdo...
                </div>
              ) : (
                '🚀 Gerar Conteúdo Profissional'
              )}
            </button>
          </form>
        </div>

        {/* Generated Content */}
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold text-gray-900">📝 Conteúdo Gerado</h2>
            {generatedContent && (
              <button
                onClick={copyToClipboard}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded font-medium"
              >
                📋 Copiar Tudo
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-gray-600 text-center">
                🤖 IA está criando seu conteúdo profissional...<br/>
                <span className="text-sm">Isso pode levar 10-30 segundos</span>
              </p>
            </div>
          ) : generatedContent ? (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <pre className="whitespace-pre-wrap text-sm text-gray-800">
                  {generatedContent}
                </pre>
              </div>
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2">🎉 Conteúdo Pronto para Usar!</h4>
                <div className="text-sm text-green-700 space-y-1">
                  <p>• Copie o texto acima</p>
                  <p>• Cole no Instagram, Facebook, ou blog</p>
                  <p>• Adicione seu link de afiliado</p>
                  <p>• Publique e comece a ganhar! 💰</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🤖</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">IA Pronta para Trabalhar</h3>
              <p className="text-gray-600">Preencha o formulário e clique em gerar para criar conteúdo profissional que vende!</p>
            </div>
          )}
        </div>
      </div>

      {/* Content History */}
      {contentHistory.length > 0 && (
        <div className="mt-12 bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold text-gray-900 mb-6">📚 Histórico de Conteúdo</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {contentHistory.map(item => (
              <div key={item.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-start mb-2">
                  <h3 className="font-medium text-gray-900 text-sm">{item.product_name}</h3>
                  <span className="text-xs text-gray-500 capitalize">{item.content_type}</span>
                </div>
                <p className="text-xs text-gray-600 mb-3 line-clamp-3">
                  {item.content.substring(0, 100)}...
                </p>
                <button
                  onClick={() => copyFromHistory(item.content)}
                  className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                >
                  📋 Copiar →
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Success Examples */}
      <div className="mt-12 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h2 className="text-xl font-semibold text-yellow-800 mb-6">🏆 Exemplos de Sucesso</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded-lg">
            <h3 className="font-semibold mb-2">📱 Post que Gerou 50 Vendas:</h3>
            <div className="bg-gray-100 p-3 rounded text-sm italic">
              "🔥 GENTE! Testei este curso de marketing digital e não acredito no resultado! 
              Em 2 semanas já recuperei o investimento. Link nos comentários 👇"
            </div>
            <p className="text-xs text-yellow-700 mt-2">💰 Resultado: R$ 6.000 em comissões</p>
          </div>
          
          <div className="bg-white p-4 rounded-lg">
            <h3 className="font-semibold mb-2">⭐ Resenha que Viralizou:</h3>
            <div className="bg-gray-100 p-3 rounded text-sm italic">
              "Análise HONESTA do iPhone 15: Comprei, usei 30 dias, e vou te contar a VERDADE. 
              Prós, contras, vale a pena? Tudo no post completo..."
            </div>
            <p className="text-xs text-yellow-700 mt-2">📊 Resultado: 10.000 visualizações, 80 vendas</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContentGenerator;